import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { IWidget, LoadingIndicatorService, ToastrService } from '@kpmg-tax/ui';
import { SessionService } from '../../../shared/session-service/session.service';
import { User } from '../../auth';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AdminClientUsersService } from 'src/app/admin/admin-client-users/admin-client-users.service';
import { UserDetail } from '../../../shared/models/get-all-users-response.model';
import { Location } from '@angular/common';
import { EndSessionRequest } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { ImpersonationService } from '../../../shared/impersonation/impersonation.service';
import { UserDataService } from '../../services/user-data.service';
import { filter, map, startWith } from 'rxjs/operators';
import { role } from '../../../shared/constants/role-enum';

@Component({
  selector: 'user-menu',
  templateUrl: 'user-menu.component.html',
  styleUrls: ['user-menu.component.scss']
})
export class UserMenuComponent implements OnInit, OnDestroy, IWidget {
  private _subscriptions: Subscription[] = [];
  user: User = {
    firstName: '',
    lastName: '',
    email: ''
  };
  // IWidget interface properties
  data: any;
  isSignInAsClientFlag = false;
  clientSMEList: UserDetail[];
  filteredSMEs: UserDetail[];

  isHomeScreen$ = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map(() => window.location.pathname === '/home'),
    // InitialNavigation does not trigger a NavigationEnd event, start with the same check
    startWith(window.location.pathname === '/home'),
  );

  isAdminScreen$ = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map(() => window.location.pathname === '/admin/user' || window.location.pathname === '/admin/master-activities' || window.location.pathname === '/admin/download-reports'),
    startWith(window.location.pathname === '/admin'),
  );

  isClientManager$ = this.userDataService.userDetails$.pipe(
    map(({ roleId }) => roleId === role.clientManager)
  );

  isSme$ = this.userDataService.userDetails$.pipe(
    map(({ roleId }) => roleId === role.respondent)
  );

  isInternalUser$ = this.userDataService.userDetails$.pipe(
    map(({ roleId }) =>
      roleId === role.admin || roleId === role.champion,
    )
  );

  showViewAsOption$ = combineLatest([
    this.isHomeScreen$,
    this.isInternalUser$,
    this.isClientManager$,
    this.isSme$,
    this.isAdminScreen$
  ]).pipe(
    map(([isHomeScreen, isInternalUser, isClientManager, isSme, isAdmin]) => {
      if (isHomeScreen || isSme || isClientManager || isAdmin) {
        return false;
      }
      return isInternalUser && !isHomeScreen;
    })
  );

  constructor(
    private _msalService: MsalService,
    private _activatedRoute: ActivatedRoute,
    public sessionService: SessionService,
    public _clientUsersService: AdminClientUsersService,
    public toastrService: ToastrService,
    private loadingService: LoadingIndicatorService,
    private location: Location,
    private impersonationService: ImpersonationService,
    private userDataService: UserDataService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this._subscriptions.push(
      this.userDataService.userDetails$.subscribe((userDetails) => {
        this.user = userDetails;
      }),

      combineLatest([
        this.impersonationService.smeUserDetails$,
        this.impersonationService.clientManagerModeActive$
      ]).subscribe(([smeUserDetails, isClientManagerViewActive]) => {
        const isEnabled = (!!smeUserDetails || isClientManagerViewActive);
        this.isSignInAsClientFlag = isEnabled;
      }),
    );

  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  logout(): void {
    this.sessionService.clearSession();
    localStorage.clear();
    const activeAccount = this._msalService.instance.getActiveAccount();
    const logoutRequest: EndSessionRequest = {} as EndSessionRequest;
    logoutRequest.account = activeAccount;
    this._msalService.logoutRedirect(logoutRequest);
  }

  activateClientManagerMode() {
    // this.impersonationService.activateClientManagerView();
    // this.sessionService.setSession('previousURL', this.router.url);
    const studyId = this.impersonationService.studyId;
    const clientId = this.sessionService.getSession('clientId');
    if (!environment.production) {
      // window.location.assign(`${environment.rcnSurveyUrl}?viewAs=client-manager&studyId=${studyId}&clientId=${clientId}`);
      window.location.assign(`${environment.rcnSurveyUrl}client-manager/study/${studyId}/overview?viewAs=client-manager&studyId=${studyId}&clientId=${clientId}`);
    } else {
      let envName = `${environment.envName}`;
      const isQAAdHocEnv = envName.trim().length > 1 ? true : false;
      if (isQAAdHocEnv) {
        // window.location.assign(`${environment.rcnUrl}rcnsurvey${envName.substring(1)}/?viewAs=client-manager&studyId=${studyId}&clientId=${clientId}`);
        window.location.assign(`${environment.rcnUrl}rcnsurvey${envName.substring(1)}client-manager/study/${studyId}/overview?viewAs=client-manager&studyId=${studyId}&clientId=${clientId}`);
      } else {
        // window.location.assign(`${environment.rcnUrl}rcnsurvey${envName}/?viewAs=client-manager&studyId=${studyId}&clientId=${clientId}`);
        window.location.assign(`${environment.rcnUrl}rcnsurvey${envName}client-manager/study/${studyId}/overview?viewAs=client-manager&clientId=${clientId}`)
      }
    }
  }


  activateViewAsSME(viewAs: number) {
    const studyId = this.impersonationService.studyId;

    if (!environment.production) {
      window.location.assign(`${environment.rcnSurveyUrl}?viewAs=${viewAs}&engagementId=${studyId}`)
    } else {
      let envName = `${environment.envName}`;
      window.location.assign(`${environment.rcnUrl}rcnsurvey${envName.substring(1)}/?viewAs=${viewAs}&engagementId=${studyId}`)
    }

  }

  getAvailableSmeUsers() {
    this.loadingService.display(true, true);
    this.isSignInAsClientFlag = false;
    const clientId = this.sessionService.getSession('clientId');
    if (clientId !== null) {
      this._clientUsersService.GetUser(parseInt(clientId, 10)).subscribe(
        data => {
          this.loadingService.display(false);
          this.clientSMEList = data.result;
          this.filteredSMEs = [...this.clientSMEList];
        });
    }
  }

  onSearchSMEs(searchValue: string) {
    searchValue = searchValue.toLowerCase();
    if (searchValue !== '') {
      this.filteredSMEs = [];
      this.filteredSMEs = this.clientSMEList.filter(SME => SME.displayName.toLowerCase().indexOf(searchValue) !== -1);
    } else {
      this.filteredSMEs = this.clientSMEList;
    }
  }

  revertClientSME() {
    this.isSignInAsClientFlag = true;
  }


  @HostListener('document:click', ['$event.target.dataset', '$event']) onDocumentClick(dataSet: DOMStringMap, event: Event) {
    if (dataSet.clientsmeinput !== undefined && dataSet.clientsmeinput === 'clientsmeinput') {
      event.stopPropagation();
    } else if (dataSet.clientsmesp !== undefined && dataSet.clientsmesp === 'clientsmesp') {
      this.isSignInAsClientFlag = true;
    } else {
      this.isSignInAsClientFlag = true;
    }
  }
}
